import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  fetchActionItemsByAccountId,
  fetchActionItemById,
  createActionItem,
  updateActionItem,
  createActionItemProgress,
  fetchActionItemRelatedObjects,
  fetchActionItemProgress,
  fetchUpcomingActionItemsByAccountId,
  fetchAllActionItems,
  fetchMyActionItems,
  CreateActionItemRequest,
  UpdateActionItemRequest,
  CreateActionItemProgressRequest,
  fetchOpenActionItemsByUserId,
  deleteActionItem,
} from "@/services/frontendServices/actionItemsServices";
import { ActionItemWithAuditDto } from "@/types";
import { RelatedObjectType } from "@/enums";
import { accountKeys } from "./useAccounts";
import { getErrorMessage, showErrorToast } from "@/lib/error-handling";

// Keys for queries, to maintain consistency
export const actionItemKeys = {
  all: ["action-items"] as const,
  lists: () => [...actionItemKeys.all, "list"] as const,
  list: (accountId: string) => [...actionItemKeys.lists(), accountId] as const,
  details: () => [...actionItemKeys.all, "detail"] as const,
  detail: (id: string) => [...actionItemKeys.details(), id] as const,
  openByUser: (userId: string) =>
    [...actionItemKeys.all, "openByUser", userId] as const,
  mine: () => [...actionItemKeys.all, "mine"] as const,
};

/**
 * Hook to fetch action items for a specific account
 */
export const useActionItems = (accountId: string) => {
  return useQuery({
    queryKey: actionItemKeys.list(accountId),
    queryFn: () => fetchActionItemsByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch a specific action item by ID
 */
export const useActionItem = (actionItemId: string) => {
  return useQuery({
    queryKey: actionItemKeys.detail(actionItemId),
    queryFn: () => fetchActionItemById(actionItemId),
    enabled: !!actionItemId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to create a new action item
 */
export const useCreateActionItem = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ActionItemWithAuditDto,
    Error,
    CreateActionItemRequest & { accountId: string }
  >({
    mutationFn: ({ accountId, ...data }) => createActionItem(accountId, data),
    onSuccess: (newActionItem, variables) => {
      // Invalidate and refetch action items for the account
      queryClient.invalidateQueries({
        queryKey: actionItemKeys.list(variables.accountId),
      });

      // Invalidate upcoming action items cache to ensure it updates when new action items are created
      queryClient.invalidateQueries({
        queryKey: [...actionItemKeys.list(variables.accountId), "upcoming"],
      });

      // Invalidate account cache to update last updated date
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(variables.accountId),
      });

      // Invalidate org-wide list
      queryClient.invalidateQueries({
        queryKey: [...actionItemKeys.all, "global"],
      });

      queryClient.invalidateQueries({ queryKey: actionItemKeys.mine() });

      toast.success("Action item created successfully!", {
        description: `"${newActionItem.title}" has been created.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating action item",
        defaultMessage: "Failed to create action item",
        duration: 5000,
      });
    },
  });
};

/**
 * Hook to update existing action item
 */
export const useUpdateActionItem = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation<
    ActionItemWithAuditDto,
    Error,
    { actionItemId: string; formData: UpdateActionItemRequest }
  >({
    mutationFn: ({ actionItemId, formData }) =>
      updateActionItem(actionItemId, formData),
    onSuccess: (updatedActionItem, variables) => {
      const { actionItemId, formData } = variables;
      const accountId = updatedActionItem.account.id;

      if (accountId) {
        queryClient.setQueryData<ActionItemWithAuditDto[]>(
          actionItemKeys.list(accountId),
          (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((actionItem) =>
              actionItem.id === updatedActionItem.id
                ? updatedActionItem
                : actionItem
            );
          }
        );

        queryClient.setQueryData<ActionItemWithAuditDto[]>(
          [...actionItemKeys.all, "global"],
          (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((actionItem) =>
              actionItem.id === updatedActionItem.id
                ? updatedActionItem
                : actionItem
            );
          }
        );

        queryClient.invalidateQueries({
          queryKey: actionItemKeys.list(accountId),
        });

        queryClient.invalidateQueries({
          queryKey: [...actionItemKeys.list(accountId), "upcoming"],
        });

        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });
      }

      queryClient.invalidateQueries({
        queryKey: actionItemKeys.detail(actionItemId),
      });

      queryClient.invalidateQueries({
        queryKey: ["actionItemRelatedObjects", actionItemId],
      });

      // Invalidate org-wide list
      queryClient.invalidateQueries({
        queryKey: [...actionItemKeys.all, "global"],
      });

      queryClient.invalidateQueries({ queryKey: actionItemKeys.mine() });

      if (!showSuccessToast) return;

      // Show specific messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archived" : "unarchived";
        toast.success(`Action item ${action} successfully!`, {
          description: `Action item has been ${action}.`,
          duration: 3000,
        });
      } else {
        toast.success("Action item updated successfully!", {
          description: `Action item has been updated.`,
          duration: 3000,
        });
      }
    },
    onError: (error, variables) => {
      const errorMessage = getErrorMessage(
        error,
        "Failed to update action item"
      );
      const { formData } = variables;

      // Show specific error messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archive" : "unarchive";
        toast.error(`Failed to ${action} action item`, {
          description: errorMessage,
          duration: 5000,
        });
      } else {
        toast.error("Error updating action item", {
          description: errorMessage,
          duration: 5000,
        });
      }
    },
  });
};

interface UseActionItemRelatedObjectsParams {
  actionItemId: string;
  types: RelatedObjectType[];
}

export function useActionItemRelatedObjects({
  actionItemId,
  types,
}: UseActionItemRelatedObjectsParams) {
  return useQuery({
    queryKey: ["actionItemRelatedObjects", actionItemId, types],
    queryFn: async () => {
      return fetchActionItemRelatedObjects(actionItemId, types);
    },
    enabled: !!actionItemId && !!types.length,
  });
}

// Hook to create action item progress
export const useCreateActionItemProgress = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ActionItemWithAuditDto,
    Error,
    { actionItemId: string; formData: CreateActionItemProgressRequest }
  >({
    mutationFn: ({ actionItemId, formData }) =>
      createActionItemProgress(actionItemId, formData),

    onSuccess: (updatedActionItem, variables) => {
      const { actionItemId } = variables;
      const accountId = updatedActionItem.account?.id;

      // Invalidate all related queries to force fresh data fetch
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: actionItemKeys.list(accountId),
        });

        queryClient.invalidateQueries({
          queryKey: [...actionItemKeys.list(accountId), "upcoming"],
        });

        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });
      }

      queryClient.invalidateQueries({
        queryKey: actionItemKeys.detail(actionItemId),
      });

      queryClient.invalidateQueries({
        queryKey: ["actionItemRelatedObjects", actionItemId],
      });

      queryClient.invalidateQueries({
        queryKey: ["actionItemProgress", actionItemId],
      });

      queryClient.invalidateQueries({ queryKey: actionItemKeys.mine() });

      toast.success("Action item progress updated successfully!", {
        description: `Action item progress has been updated.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error updating action item progress",
        defaultMessage: "Failed to update action item progress",
        duration: 5000,
      });
    },
  });
};

export function useActionItemProgress({
  actionItemId,
  expand,
  relatedTypes,
}: {
  actionItemId: string;
  expand?: "relatedObjects";
  relatedTypes?: string[];
}) {
  return useQuery({
    queryKey: ["actionItemProgress", actionItemId, expand, relatedTypes],
    queryFn: async () => {
      return fetchActionItemProgress(actionItemId, { expand, relatedTypes });
    },
    enabled: !!actionItemId,
  });
}

/**
 * Hook to delete an existing action item.
 * Invalidates all related caches including account data.
 */
export const useDeleteActionItem = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { actionItemId: string }>({
    mutationFn: async ({ actionItemId }) => {
      await deleteActionItem(actionItemId);
    },
    onSuccess: (_, { actionItemId }) => {
      // Cancel any outgoing refetches for this action item and related queries
      queryClient.cancelQueries({
        queryKey: actionItemKeys.detail(actionItemId),
      });
      queryClient.cancelQueries({
        queryKey: ["actionItemRelatedObjects", actionItemId],
      });
      queryClient.cancelQueries({
        queryKey: ["actionItemProgress", actionItemId],
      });

      // Remove the action item from the detail cache
      queryClient.removeQueries({
        queryKey: actionItemKeys.detail(actionItemId),
      });
      queryClient.removeQueries({
        queryKey: ["actionItemRelatedObjects", actionItemId],
      });
      queryClient.removeQueries({
        queryKey: ["actionItemProgress", actionItemId],
      });

      // Set the query data to null to prevent refetch attempts
      queryClient.setQueryData(actionItemKeys.detail(actionItemId), null);

      // Invalidate all action item lists (this covers all account lists)
      queryClient.invalidateQueries({
        queryKey: actionItemKeys.lists(),
      });

      // Invalidate org-wide list
      queryClient.invalidateQueries({
        queryKey: [...actionItemKeys.all, "global"],
      });

      // Invalidate upcoming action items
      queryClient.invalidateQueries({
        queryKey: actionItemKeys.all,
        predicate: (query) => query.queryKey.includes("upcoming"),
      });

      // Invalidate open action items by user
      queryClient.invalidateQueries({
        queryKey: actionItemKeys.all,
        predicate: (query) => query.queryKey.includes("openByUser"),
      });

      queryClient.invalidateQueries({ queryKey: actionItemKeys.mine() });

      // Invalidate all accounts to update last updated dates
      queryClient.invalidateQueries({
        queryKey: accountKeys.lists(),
      });

      toast.success("Action Item deleted successfully!", {
        description: "The action item has been deleted.",
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error deleting action item",
        defaultMessage: "Failed to delete action item",
        duration: 5000,
      });
    },
  });
};

/**
 * Hook to fetch upcoming action items for a specific account
 * Returns top 3 action items ordered by due date (nearest first)
 */
export const useUpcomingActionItems = (accountId: string) => {
  return useQuery<ActionItemWithAuditDto[], Error>({
    queryKey: [...actionItemKeys.list(accountId), "upcoming"],
    queryFn: () => fetchUpcomingActionItemsByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to get all action items across all accounts for global dashboard statistics
export const useAllActionItems = () => {
  return useQuery<ActionItemWithAuditDto[], Error>({
    queryKey: [...actionItemKeys.all, "global"],
    queryFn: () => fetchAllActionItems(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch the current user's open action items for the active
 * organization, ordered by priority (CRITICAL first) then due date ascending.
 */
export const useMyActionItems = () => {
  return useQuery<ActionItemWithAuditDto[], Error>({
    queryKey: actionItemKeys.mine(),
    queryFn: () => fetchMyActionItems(),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to get all open action items assigned to a specific user by their user ID
 *
 * @param userId - The ID of the user whose open action items are to be retrieved
 * @returns A query object containing the user's open action items
 */
export const useOpenActionItemsByUserId = (userId: string) => {
  return useQuery<ActionItemWithAuditDto[], Error>({
    queryKey: actionItemKeys.openByUser(userId),
    queryFn: async () => fetchOpenActionItemsByUserId(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};
